import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactElement, ReactNode } from 'react'

import KestrixButton from '../buttons/KestrixButton.jsx'
import { colors, spacing } from '../KestrixDesignSystem.jsx'
import { KestrixEpcRatingChip } from '../KestrixEpcRatingChip.tsx'
import { KestrixPill } from '../KestrixPill.tsx'
import { KestrixCheckCircleIcon } from '../KestrixSvgIcons.jsx'
import { colorTokens } from '../tokens/colors.ts'
import KestrixV2Typography from '../typography/KestrixTypography.tsx'
import type {
  CellDisplay,
  CellLink,
  CellValue,
  KestrixPropertyTableColumn
} from './cell-display.types.ts'
import {
  isCellLink,
  isCellPerformanceChip,
  isCellPillList,
  UNBUILT_CELL_DISPLAYS
} from './cell-display.types.ts'
import { formatSignificant } from './format-significant.ts'
import { renderPerformanceChip } from './render-performance-chip.tsx'
import { renderTonedPillList } from './render-toned-pill-list.tsx'
import { splitAddressKeepPostcode } from './split-address-keep-postcode.ts'

const EM_DASH = '—'

const ellipsisTextSx: SxProps<Theme> = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}

/** Text fallback shared by `'text'` and every unbuilt `CellDisplay`. */
const formatCellValueAsText = (value: CellValue): string => {
  if (value === null || value === undefined) {
    return EM_DASH
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? EM_DASH : value.join(', ')
  }
  if (isCellLink(value)) {
    return value.label === '' ? EM_DASH : value.label
  }
  if (isCellPerformanceChip(value)) {
    return value.label
  }
  if (isCellPillList(value)) {
    return value.pills.length === 0
      ? EM_DASH
      : value.pills.map((pill) => pill.label).join(', ')
  }
  if (typeof value === 'object') {
    const entryCount = Object.keys(value).length
    return entryCount === 0 ? EM_DASH : `${entryCount} types`
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return String(value)
}

/** The universal fallback renderer — also reused by {@link renderTonedPillList} in `render-toned-pill-list.tsx`. */
export const renderText = (value: CellValue): ReactElement => {
  const text = formatCellValueAsText(value)
  return (
    <KestrixV2Typography
      variant="bodyMedium"
      weight="regular"
      color={text === EM_DASH ? 'disabled' : 'body-neutral'}
      htmlTag="span"
      sx={ellipsisTextSx}
    >
      {text}
    </KestrixV2Typography>
  )
}

/** The Postcode column carries the postcode, so the address drops its trailing copy. */
const renderAddress = (value: CellValue): ReactElement => {
  const text = formatCellValueAsText(value)
  if (text === EM_DASH) {
    return renderText(value)
  }
  const parts = splitAddressKeepPostcode(text)
  return renderText(parts == null ? value : parts.head)
}

const renderEpcGrade = (
  value: CellValue,
  column: KestrixPropertyTableColumn
): ReactElement => (
  <KestrixEpcRatingChip
    rating={typeof value === 'string' ? value : undefined}
    variant={column.epcVariant ?? 'lodged'}
    size={24}
  />
)

/** Design-confirmed colours (ticket 023). Unknown values fall back to text. */
const SEVERITY_BADGE_COLOR: Readonly<Record<string, string>> = {
  Matched: colorTokens.global.other.green[500],
  Underperforming: colorTokens.semantic.status.error
}

const renderSeverityBadge = (value: CellValue): ReactElement => {
  if (typeof value !== 'string') {
    return renderText(value)
  }
  const color = SEVERITY_BADGE_COLOR[value]
  if (color === undefined) {
    return renderText(value)
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: '100px',
        border: `1px solid ${color}`,
        backgroundColor: colorTokens.semantic.brand.base.white
      }}
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color
        }}
      />
      <KestrixV2Typography
        variant="bodySmall"
        weight="medium"
        htmlTag="span"
        sx={{ color }}
      >
        {value}
      </KestrixV2Typography>
    </Box>
  )
}

const renderBooleanTick = (value: CellValue): ReactElement => {
  if (value !== true) {
    return renderText(null)
  }
  return <KestrixCheckCircleIcon size={16} />
}

const formatSignedDelta = (value: number): string =>
  value > 0 ? `+${value}` : value === 0 ? '+0' : String(value)

const renderNumberDelta = (value: CellValue): ReactElement => {
  if (typeof value !== 'number') {
    return renderText(value)
  }
  return (
    <KestrixV2Typography
      variant="bodyMedium"
      weight="regular"
      color="body-neutral"
      htmlTag="span"
    >
      {formatSignedDelta(value)}
    </KestrixV2Typography>
  )
}

const renderNumber = (value: CellValue): ReactElement => {
  if (typeof value !== 'number') {
    return renderText(value)
  }
  return (
    <KestrixV2Typography
      variant="bodyMedium"
      weight="regular"
      color="body-neutral"
      htmlTag="span"
    >
      {formatSignificant(value)}
    </KestrixV2Typography>
  )
}

const renderInteger = (value: CellValue): ReactElement => {
  if (typeof value !== 'number') {
    return renderText(value)
  }
  return (
    <KestrixV2Typography
      variant="bodyMedium"
      weight="regular"
      color="body-neutral"
      htmlTag="span"
    >
      {value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
    </KestrixV2Typography>
  )
}

const renderPillGroup = (
  values: readonly string[],
  pillProps?: {
    readonly selectedColor: string
    readonly selectedBorder: string
  }
): ReactElement => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing[1] }}>
    {values.map((value) => (
      <KestrixPill key={value} label={value} size="sm" {...pillProps} />
    ))}
  </Box>
)

const renderMeasureChips = (value: CellValue): ReactElement => {
  if (!Array.isArray(value)) {
    return renderText(value)
  }
  if (value.length === 0) {
    return renderText(null)
  }
  return renderPillGroup(value)
}

const ATTENTION_BADGE_COLOR = {
  selectedColor: colorTokens.global.other.cyan[500],
  selectedBorder: colorTokens.global.other.cyan[600]
}

const renderAttentionBadges = (value: CellValue): ReactElement => {
  if (!Array.isArray(value)) {
    return renderText(value)
  }
  if (value.length === 0) {
    return renderText(null)
  }
  return renderPillGroup(value, ATTENTION_BADGE_COLOR)
}

/** Stored 0–1, shown as a plain 0–100 whole number (no grade chip). */
const renderFractionPercent = (value: CellValue): ReactElement => {
  if (typeof value !== 'number') {
    return renderText(value)
  }
  return (
    <KestrixV2Typography
      variant="bodyMedium"
      weight="regular"
      color="body-neutral"
      htmlTag="span"
    >
      {Math.round(value * 100)}
    </KestrixV2Typography>
  )
}

/** Stored 0–1, shown as a 0–100 whole number. Colour bands match the label. */
const modelScoreDisplayValue = (value: number): number => Math.round(value * 100)

const modelScoreColour = (displayScore: number): string => {
  if (displayScore >= 60) {
    return colors.epc.B
  }
  if (displayScore >= 30) {
    return colors.epc.E
  }
  return colors.epc.G
}

const renderModelScore = (value: CellValue): ReactElement => {
  if (typeof value !== 'number') {
    return renderText(value)
  }
  const displayScore = modelScoreDisplayValue(value)
  const colour = modelScoreColour(displayScore)
  return (
    <Box
      data-testid="property-table-row-model-score"
      sx={{
        backgroundColor: colour,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: '4px',
        boxSizing: 'border-box'
      }}
    >
      <KestrixV2Typography
        variant="bodySmall"
        weight="semibold"
        color="body-neutral"
        htmlTag="span"
        sx={{ color: 'white', lineHeight: 1 }}
      >
        {displayScore}
      </KestrixV2Typography>
    </Box>
  )
}

/**
 * A button that navigates to the cell's own URL in a new tab.
 *
 * Both the label and the URL come from the cell value, so one `'navButton'`
 * column can point at a different destination per row without the row
 * knowing what those destinations mean.
 *
 * Opens through `window.open` rather than an anchor `href` because
 * `KestrixButton` does not declare anchor props. That matches how the APV
 * area tables already deeplink, at the cost of middle-click and
 * copy-link-address on the button.
 *
 * A value that is not a {@link CellLink} falls back to text rather than
 * rendering a button that goes nowhere.
 */
export type RenderPropertyTableCellOptions = {
  readonly onNavButtonClick?: (link: CellLink) => void
}

const renderNavButton = (
  value: CellValue,
  onNavButtonClick?: (link: CellLink) => void
): ReactElement => {
  if (!isCellLink(value)) {
    return renderText(value)
  }
  return (
    <KestrixButton
      size="small"
      variant="default"
      onClick={() => {
        if (onNavButtonClick) {
          onNavButtonClick(value)
          return
        }
        window.open(value.href, '_blank', 'noopener,noreferrer')
      }}
      data-testid="property-table-row-nav-button"
    >
      {value.label}
    </KestrixButton>
  )
}

/** Dispatch a column's `display` mode to the matching cell renderer. */
export const renderPropertyTableCellContent = (
  column: KestrixPropertyTableColumn,
  value: CellValue,
  options?: RenderPropertyTableCellOptions
): ReactNode => {
  const display: CellDisplay = column.display
  if (UNBUILT_CELL_DISPLAYS.has(display)) {
    return renderText(value)
  }
  switch (display) {
    case 'text':
      return renderText(value)
    case 'address':
      return renderAddress(value)
    case 'number':
      return renderNumber(value)
    case 'integer':
      return renderInteger(value)
    case 'modelScore':
      return renderModelScore(value)
    case 'fractionPercent':
      return renderFractionPercent(value)
    case 'epcGrade':
      return renderEpcGrade(value, column)
    case 'severityBadge':
      return renderSeverityBadge(value)
    case 'booleanTick':
      return renderBooleanTick(value)
    case 'numberDelta':
      return renderNumberDelta(value)
    case 'measureChips':
      return renderMeasureChips(value)
    case 'attentionBadges':
      return renderAttentionBadges(value)
    case 'navButton':
      return renderNavButton(value, options?.onNavButtonClick)
    case 'retrofitMeasurePills':
      return renderTonedPillList(value)
    case 'subjectTypeBadges':
      return renderTonedPillList(value)
    case 'confidencePillList':
      return renderTonedPillList(value)
    case 'performanceChip':
      return renderPerformanceChip(value)
  }
}
